import { useAuth } from '@core/features-core/auth/client';
import Image from 'next/image';
import React, { memo } from 'react';
import { firstLetter } from '../navbar/components/user-login-button/helper';

interface UserAvatarProps {
  size?: 'small' | 'medium' | 'large';
  colorScheme?: 'light' | 'dark';
}

export const UserAvatar = memo<UserAvatarProps>(
  ({ size = 'medium', colorScheme = 'dark' }) => {
    const { userIsPremium, userIsPro, userName } = useAuth();

    const getIconPath = () => {
      if (userIsPro) {
        return '/img/pro-icon.svg';
      }
      if (userIsPremium) {
        return '/img/prime-icon.svg';
      }
      return null; // Não retorna path para usuários gratuitos
    };

    const getAvatarSize = () => {
      switch (size) {
        case 'small':
          return 32;
        case 'medium':
          return 40;
        case 'large':
          return 46;
        default:
          return 40;
      }
    };

    const getTextSize = () => {
      switch (size) {
        case 'small':
          return 'text-sm';
        case 'medium':
          return 'text-base';
        case 'large':
          return 'text-lg';
        default:
          return 'text-base';
      }
    };

    const getBackgroundColor = () => {
      if (colorScheme === 'light') {
        return 'bg-gray-200 text-gray-700';
      }
      return 'bg-gray-600 text-white';
    };

    const iconPath = getIconPath();
    const avatarSize = getAvatarSize();
    const textSize = getTextSize();
    const bgColor = getBackgroundColor();

    // Se o usuário tem ícone (Prime ou PRO), mostra o ícone
    if (iconPath) {
      return (
        <div
          className={`rounded-full flex items-center justify-center overflow-hidden`}
          style={{ width: avatarSize, height: avatarSize }}
        >
          <Image
            src={iconPath}
            alt="User avatar"
            width={avatarSize}
            height={avatarSize}
          />
        </div>
      );
    }

    // Se não tem ícone, mostra a inicial do nome
    const initial = userName ? firstLetter(userName) : '?';

    return (
      <div
        className={`rounded-full flex items-center justify-center font-semibold ${textSize} ${bgColor}`}
        style={{ width: avatarSize, height: avatarSize }}
      >
        {initial}
      </div>
    );
  }
);
